// ── Dashboard page module ─────────────────────────────────────────────────────
// Boots only when .dashboard-page is present.
// All AJAX panel URLs are read from data-url-* on the root element.

document.addEventListener('DOMContentLoaded', () => {
    const page = document.querySelector<HTMLElement>('.dashboard-page');
    if (!page) return;

    const urls = {
        attention:       page.dataset.urlAttention ?? '',
        revenue:         page.dataset.urlRevenue ?? '',
        paymentHealth:   page.dataset.urlPaymentHealth ?? '',
        departures:      page.dataset.urlDepartures ?? '',
        topDestinations: page.dataset.urlTopDestinations ?? '',
        officePerf:      page.dataset.urlOfficePerformance ?? '',
        feed:            page.dataset.urlActivityFeed ?? '',
    };

    let currentRange = (document.getElementById('dp-range-select') as HTMLSelectElement)?.value ?? 'month';
    let feedCursor = 0;
    let feedLoading = false;
    let feedExhausted = false;

    // ── Range selector ────────────────────────────────────────────────────
    const rangeSelect = document.getElementById('dp-range-select') as HTMLSelectElement | null;
    rangeSelect?.addEventListener('change', () => {
        currentRange = rangeSelect.value;
        // Update URL param in all data-url-* that include range
        const a = new URL(location.href);
        a.searchParams.set('range', currentRange);
        history.replaceState(null, '', a.toString());

        loadRevenueSeries();
        loadTopDestinations();
        loadOfficePerformance();
    });

    // ── Utility ───────────────────────────────────────────────────────────
    async function fetchJSON<T>(url: string): Promise<T | null> {
        try {
            const r = await fetch(url, { headers: { Accept: 'application/json' } });
            const j = await r.json();
            return j.success ? (j.data as T) : null;
        } catch { return null; }
    }

    function buildUrl(base: string, extra?: Record<string, string | null | undefined>): string {
        const u = new URL(base, location.origin);
        if (extra) {
            for (const [k, v] of Object.entries(extra)) {
                if (v != null) u.searchParams.set(k, v);
            }
        }
        return u.toString();
    }

    function clearSkeleton(el: HTMLElement) {
        el.querySelectorAll<HTMLElement>('.dp-skeleton').forEach(s => s.remove());
    }

    // ── Attention queue ───────────────────────────────────────────────────
    interface AttentionItem {
        severity: string;
        kind: string;
        title: string;
        count: number;
        sub: string;
        ctaLabel: string;
        ctaHref: string;
    }

    const ICONS_ATTENTION: Record<string, string> = {
        overdue:     `<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>`,
        departing48: `<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M17.8 19.2L16 11l3.5-3.5C21 6 21.5 4 21 3c-1-.5-3 0-4.5 1.5L13 8 4.8 6.2c-.5-.1-.9.1-1.1.5l-.3.5c-.2.5-.1 1 .3 1.3L9 12l-2 3H4l-1 1 3 2 2 3 1-1v-3l3-2 3.5 5.3c.3.4.8.5 1.3.3l.5-.2c.4-.3.6-.7.5-1.2z"/></svg>`,
        drafts:      `<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>`,
    };

    const ARROW_SVG = `<svg width="11" height="11" viewBox="0 0 12 12" fill="none"><path d="M2 6h8M7 3l3 3-3 3" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg>`;

    async function loadAttention() {
        const container = document.getElementById('dp-attention');
        if (!container) return;

        const items = await fetchJSON<AttentionItem[]>(buildUrl(urls.attention));
        container.innerHTML = '';

        if (!items || items.length === 0) {
            container.style.display = 'none';
            return;
        }

        container.style.display = '';
        for (const item of items) {
            const icon = ICONS_ATTENTION[item.kind] ?? ICONS_ATTENTION.drafts;
            container.insertAdjacentHTML('beforeend', `
                <div class="dp-alert-card ${item.severity}">
                    <div class="dp-alert-icon">${icon}</div>
                    <div class="dp-alert-content">
                        <div class="dp-alert-title">
                            ${item.title}
                            <span class="dp-alert-count">${item.count}</span>
                        </div>
                        <div class="dp-alert-sub">${item.sub}</div>
                        <a class="dp-alert-cta" href="${item.ctaHref}">${item.ctaLabel} ${ARROW_SVG}</a>
                    </div>
                </div>`);
        }
    }

    // ── Revenue chart ─────────────────────────────────────────────────────
    interface RevenuePoint { m: string; booked: number; collected: number; contracts: number; }

    async function loadRevenueSeries() {
        const body = document.getElementById('dp-revenue-body');
        if (!body) return;

        body.className = 'dp-skeleton';
        body.innerHTML = '';
        body.style.height = '300px';

        const data = await fetchJSON<RevenuePoint[]>(buildUrl(urls.revenue, { range: currentRange }));
        body.className = '';
        body.style.height = '';

        if (!data || data.length === 0) {
            body.innerHTML = '<div class="dp-empty-panel">No revenue data for this period.</div>';
            return;
        }

        renderRevenueChart(body, data);
    }

    function renderRevenueChart(container: HTMLElement, data: RevenuePoint[]) {
        const W = 700, H = 240;
        const padL = 44, padR = 16, padT = 14, padB = 32;
        const innerW = W - padL - padR;
        const innerH = H - padT - padB;
        const maxVal = Math.max(...data.map(d => Math.max(d.booked, d.collected))) * 1.1 || 1;
        const barGroupW = innerW / data.length;
        const barW = Math.min(20, barGroupW * 0.32);
        const gap = 4;
        const yToPx = (v: number) => padT + innerH - (v / maxVal) * innerH;
        const yTicks = [0, 0.25, 0.5, 0.75, 1].map(t => Math.round(maxVal * t));

        const total = data.reduce((s, d) => s + d.booked, 0);
        const collected = data.reduce((s, d) => s + d.collected, 0);
        const totalContracts = data.reduce((s, d) => s + d.contracts, 0);

        const fmt = (v: number) => new Intl.NumberFormat('sr-Latn-RS', { style: 'decimal', maximumFractionDigits: 0 }).format(v);

        const legend = `
            <div class="dp-chart-legend">
                <div class="legend-item"><span class="swatch" style="background:#F5A623"></span>Booked<span class="val">${fmt(total)}</span></div>
                <div class="legend-item"><span class="swatch" style="background:#262B40"></span>Collected<span class="val">${fmt(collected)}</span></div>
                <div class="legend-item" style="margin-left:auto"><span class="swatch" style="background:#05A677;border-radius:50%;width:8px;height:8px"></span>Contracts<span class="val">${totalContracts}</span></div>
            </div>`;

        // Build SVG pieces
        const gridLines = yTicks.map((t, i) =>
            `<line x1="${padL}" y1="${yToPx(t)}" x2="${W - padR}" y2="${yToPx(t)}" stroke="#EDF0F7" stroke-width="1" stroke-dasharray="${i === 0 ? '0' : '3 3'}"/>
             <text x="${padL - 8}" y="${yToPx(t) + 4}" text-anchor="end" font-size="10.5" font-weight="700" fill="#8896B3">${(t / 1000).toFixed(0)}k</text>`
        ).join('');

        // Contract line
        const cmax = Math.max(...data.map(d => d.contracts)) || 1;
        const linePoints = data.map((d, i) => {
            const cx = padL + barGroupW * i + barGroupW / 2;
            const cy = padT + innerH - (d.contracts / cmax) * innerH * 0.6 - 10;
            return `${cx},${cy}`;
        }).join(' ');

        const bars = data.map((d, i) => {
            const cx = padL + barGroupW * i + barGroupW / 2;
            const x1 = cx - barW - gap / 2;
            const x2 = cx + gap / 2;
            const h1 = (d.booked / maxVal) * innerH;
            const h2 = (d.collected / maxVal) * innerH;
            const cy = padT + innerH - (d.contracts / cmax) * innerH * 0.6 - 10;

            return `
                <g class="dp-bar-group" data-i="${i}">
                    <rect x="${cx - barGroupW / 2}" y="${padT}" width="${barGroupW}" height="${innerH}" fill="transparent" style="cursor:pointer" class="dp-hover-zone"/>
                    <rect x="${x1}" y="${padT + innerH - h1}" width="${barW}" height="${h1}" rx="4" fill="#F5A623" class="dp-bar-booked"/>
                    <rect x="${x2}" y="${padT + innerH - h2}" width="${barW}" height="${h2}" rx="4" fill="#262B40" class="dp-bar-coll"/>
                    <circle cx="${cx}" cy="${cy}" r="3.5" fill="#05A677" stroke="#fff" stroke-width="2" class="dp-bar-dot"/>
                    <text x="${cx}" y="${H - 10}" text-anchor="middle" font-size="11" font-weight="700" fill="#66799E" style="text-transform:uppercase;letter-spacing:.5px">${d.m}</text>
                </g>`;
        }).join('');

        const svg = `<svg class="dp-chart-svg" viewBox="0 0 ${W} ${H}" preserveAspectRatio="none">
            ${gridLines}
            <polyline points="${linePoints}" fill="none" stroke="#05A677" stroke-width="1.6" opacity="0.65"/>
            ${bars}
        </svg>`;

        const tooltip = `<div class="dp-chart-tooltip" id="dp-chart-tt"></div>`;

        container.innerHTML = `<div class="dp-chart-wrap" style="position:relative">${legend}${svg}${tooltip}</div>`;

        // Hover interactions
        const tt = container.querySelector<HTMLElement>('#dp-chart-tt')!;
        const wrap = container.querySelector<HTMLElement>('.dp-chart-wrap')!;

        container.querySelectorAll<SVGElement>('.dp-hover-zone').forEach((zone, i) => {
            zone.addEventListener('mouseenter', (e) => {
                const d = data[i];
                const wRect = wrap.getBoundingClientRect();
                const zRect = zone.getBoundingClientRect();
                const x = zRect.left - wRect.left + zRect.width / 2;
                const y = zRect.top - wRect.top;

                // highlight bars
                const g = container.querySelectorAll<SVGGElement>('.dp-bar-group')[i];
                g.querySelector<SVGRectElement>('.dp-bar-booked')!.setAttribute('fill', '#D4881A');
                g.querySelector<SVGRectElement>('.dp-bar-coll')!.setAttribute('fill', '#1A1F2E');
                g.querySelector<SVGCircleElement>('.dp-bar-dot')!.setAttribute('r', '5');

                tt.innerHTML = `
                    <div class="tt-month">${d.m}</div>
                    <div class="tt-row"><span class="dot" style="background:#F5A623"></span> Booked &nbsp;<strong>${fmt(d.booked)}</strong></div>
                    <div class="tt-row"><span class="dot" style="background:#fff"></span> Collected &nbsp;<strong>${fmt(d.collected)}</strong></div>
                    <div class="tt-row"><span class="dot" style="background:#05A677"></span> Contracts &nbsp;<strong>${d.contracts}</strong></div>`;
                tt.style.left = `${x}px`;
                tt.style.top = `${y - 6}px`;
                tt.classList.add('show');
            });

            zone.addEventListener('mouseleave', () => {
                const g = container.querySelectorAll<SVGGElement>('.dp-bar-group')[i];
                g.querySelector<SVGRectElement>('.dp-bar-booked')!.setAttribute('fill', '#F5A623');
                g.querySelector<SVGRectElement>('.dp-bar-coll')!.setAttribute('fill', '#262B40');
                g.querySelector<SVGCircleElement>('.dp-bar-dot')!.setAttribute('r', '3.5');
                tt.classList.remove('show');
            });
        });
    }

    // ── Payment donut ─────────────────────────────────────────────────────
    interface PaymentHealthItem { key: string; label: string; value: number; contracts: number; color: string; }

    async function loadPaymentDonut() {
        const body = document.getElementById('dp-donut-body');
        if (!body) return;

        body.className = 'dp-skeleton';
        body.innerHTML = '';
        body.style.height = '300px';

        const data = await fetchJSON<PaymentHealthItem[]>(buildUrl(urls.paymentHealth));
        body.className = '';
        body.style.height = '';

        if (!data) return;

        const total = data.reduce((s, d) => s + d.value, 0);
        const totalContracts = data.reduce((s, d) => s + d.contracts, 0);

        const subEl = document.getElementById('dp-donut-sub');
        if (subEl) subEl.textContent = (subEl.dataset.format ?? '{0} active contracts').replace('{0}', String(totalContracts));

        const r = 70, cx = 90, cy = 90, circ = 2 * Math.PI * r;
        let offset = 0;
        const segs = data.map(d => {
            const frac = total > 0 ? d.value / total : 0;
            const len = circ * frac;
            const seg = { ...d, len, offset, pct: frac * 100 };
            offset += len;
            return seg;
        });

        const circles = segs.map(s =>
            `<circle cx="${cx}" cy="${cy}" r="${r}" fill="none" stroke="${s.color}" stroke-width="22"
             stroke-dasharray="${s.len} ${circ - s.len}" stroke-dashoffset="${-s.offset}"
             transform="rotate(-90 ${cx} ${cy})" stroke-linecap="butt"/>`
        ).join('');

        const fmt = (v: number) => `${(v / 1000).toFixed(0)}k`;
        const fmtFull = (v: number) => new Intl.NumberFormat('sr-Latn-RS', { style: 'decimal', maximumFractionDigits: 2 }).format(v);

        const legend = segs.map(s => `
            <div class="dp-donut-row">
                <span class="sq" style="background:${s.color}"></span>
                <span class="lbl">${s.label}</span>
                <span class="val">${fmtFull(s.value)}</span>
                <span class="pct">${s.pct.toFixed(0)}%</span>
            </div>`).join('');

        body.innerHTML = `
            <div class="dp-donut-wrap">
                <div class="dp-donut">
                    <svg width="180" height="180" viewBox="0 0 180 180">
                        <circle cx="${cx}" cy="${cy}" r="${r}" fill="none" stroke="#EDF0F7" stroke-width="22"/>
                        ${circles}
                    </svg>
                    <div class="dp-donut-center">
                        <div class="total-val">${fmt(total)}</div>
                        <div class="total-label">${document.getElementById('dp-donut-panel')?.dataset.locBooked ?? 'Booked'}</div>
                    </div>
                </div>
                <div class="dp-donut-legend">${legend}</div>
            </div>`;
    }

    // ── Departures timeline ───────────────────────────────────────────────
    interface DepartureItem {
        contractId: number;
        day: string; rel: string;
        travellerName: string; destination: string;
        pax: number; transport: string;
        initials: string; color: string;
        today: boolean; urgent: boolean;
    }

    const TRANSPORT_ICONS: Record<string, string> = {
        flight: `<svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M17.8 19.2L16 11l3.5-3.5C21 6 21.5 4 21 3c-1-.5-3 0-4.5 1.5L13 8 4.8 6.2c-.5-.1-.9.1-1.1.5l-.3.5c-.2.5-.1 1 .3 1.3L9 12l-2 3H4l-1 1 3 2 2 3 1-1v-3l3-2 3.5 5.3c.3.4.8.5 1.3.3l.5-.2c.4-.3.6-.7.5-1.2z"/></svg>`,
        bus:    `<svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M8 6v6"/><path d="M15 6v6"/><path d="M2 12h19.6"/><rect x="3" y="4" width="18" height="16" rx="2"/></svg>`,
        car:    `<svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M5 17H3a2 2 0 01-2-2V5a2 2 0 012-2h11l5 5v9a2 2 0 01-2 2h-3"/><circle cx="7.5" cy="17.5" r="2.5"/><circle cx="17.5" cy="17.5" r="2.5"/></svg>`,
    };

    async function loadDepartures() {
        const strip = document.getElementById('dp-departures-strip');
        const panel = document.getElementById('dp-departures-panel');
        if (!strip || !panel) return;

        strip.innerHTML = '';

        const data = await fetchJSON<DepartureItem[]>(buildUrl(urls.departures));

        if (!data || data.length === 0) {
            panel.style.display = 'none';
            return;
        }

        panel.style.display = '';
        for (const d of data) {
            const cls = d.today ? 'today' : d.urgent ? 'urgent' : '';
            const tIcon = TRANSPORT_ICONS[d.transport] ?? TRANSPORT_ICONS.flight;
            strip.insertAdjacentHTML('beforeend', `
                <a class="dp-dep-card ${cls}" href="/Contract/Details/${d.contractId}">
                    <div class="dp-dep-day">${d.day}<span class="relative">${d.rel}</span></div>
                    <div class="dp-dep-dest">${d.destination}</div>
                    <div class="dp-dep-meta">${tIcon}<span>${d.pax} pax</span></div>
                    <div class="dp-dep-foot">
                        <div class="dp-dep-avatar" style="background:${d.color}">${d.initials}</div>
                        <div class="dp-dep-trav">${d.travellerName}</div>
                        <div class="dp-dep-pax">→</div>
                    </div>
                </a>`);
        }
    }

    // ── Top destinations ──────────────────────────────────────────────────
    interface DestinationItem { name: string; revenue: number; contracts: number; pct: number; color: string; }

    async function loadTopDestinations() {
        const body = document.getElementById('dp-destinations-body');
        if (!body) return;

        body.className = 'dp-skeleton';
        body.innerHTML = '';
        body.style.height = '260px';

        const data = await fetchJSON<DestinationItem[]>(buildUrl(urls.topDestinations, { range: currentRange }));
        body.className = '';
        body.style.height = '';

        if (!data || data.length === 0) {
            body.innerHTML = '<div class="dp-empty-panel">No destination data for this period.</div>';
            return;
        }

        const fmtFull = (v: number) => new Intl.NumberFormat('sr-Latn-RS', { style: 'decimal', maximumFractionDigits: 2 }).format(v);

        const contractsFmt = document.getElementById('dp-destinations-panel')?.dataset.locContractsFmt ?? '{0} contracts';
        body.innerHTML = `<div class="dp-bar-list">${data.map((d, i) => `
            <div class="dp-bar-row">
                <div class="dp-bar-rank">#${i + 1}</div>
                <div class="dp-bar-main">
                    <div class="dp-bar-label">
                        <span>${d.name}</span>
                        <span class="sub">${contractsFmt.replace('{0}', String(d.contracts))}</span>
                    </div>
                    <div class="dp-bar"><span style="width:${d.pct}%;background:${d.color}"></span></div>
                </div>
                <div class="dp-bar-amount">${fmtFull(d.revenue)}</div>
            </div>`).join('')}</div>`;
    }

    // ── Office performance ────────────────────────────────────────────────
    interface OfficeRow { name: string; meta: string; flag: string; contracts: number; revenue: number; delta: string; deltaDir: string; }

    async function loadOfficePerformance() {
        const body = document.getElementById('dp-offices-body');
        if (!body) return;

        body.className = 'dp-skeleton';
        body.innerHTML = '';
        body.style.height = '200px';

        const data = await fetchJSON<OfficeRow[]>(buildUrl(urls.officePerf, { range: currentRange }));
        body.className = '';
        body.style.height = '';

        if (!data || data.length === 0) {
            body.innerHTML = '<div class="dp-empty-panel">No office data available.</div>';
            return;
        }

        const triSvg = (dir: string) =>
            `<svg width="9" height="9" viewBox="0 0 10 10" fill="none" style="transform:${dir === 'down' ? 'rotate(180deg)' : 'none'}"><path d="M5 2L8 7H2L5 2Z" fill="currentColor"/></svg>`;

        const fmtFull = (v: number) => new Intl.NumberFormat('sr-Latn-RS', { style: 'decimal', maximumFractionDigits: 2 }).format(v);

        const rows = data.map(o => `
            <div class="dp-office-row">
                <div class="dp-office-name">
                    <div class="dp-office-flag">${o.flag}</div>
                    <div><div>${o.name}</div><span class="meta">${o.meta}</span></div>
                </div>
                <div class="dp-office-num">${o.contracts}</div>
                <div class="dp-office-num">${fmtFull(o.revenue)}</div>
                <div class="dp-office-delta">
                    ${o.delta ? `<span class="pill ${o.deltaDir}">${triSvg(o.deltaDir)}${o.delta.replace(/[+\-−]/g, '')}</span>` : '<span class="pill flat">–</span>'}
                </div>
            </div>`).join('');

        body.innerHTML = `<div class="dp-office-table">
            <div class="dp-office-row head">
                <div class="dp-office-name">Office</div>
                <div class="dp-office-num">Contracts</div>
                <div class="dp-office-num">Revenue</div>
                <div class="dp-office-num">Δ MoM</div>
            </div>
            ${rows}
        </div>`;
    }

    // ── Activity feed ─────────────────────────────────────────────────────
    interface FeedItem { type: string; icon: string; html: string; time: string; actorName: string; actorInitials: string; actorColor: string; href?: string; }

    const FEED_ICONS: Record<string, string> = {
        check: `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg>`,
        doc:   `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>`,
        warn:  `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>`,
        user:  `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="8" r="4"/><path d="M4 21c0-4.4 3.6-7 8-7s8 2.6 8 7"/></svg>`,
    };

    function renderFeedItems(items: FeedItem[], container: HTMLElement) {
        for (const f of items) {
            const icon = FEED_ICONS[f.icon] ?? FEED_ICONS.doc;
            const el = document.createElement('a');
            el.className = 'dp-feed-item';
            el.href = f.href ?? '#';
            el.innerHTML = `
                <div class="dp-feed-icon ${f.type}">${icon}</div>
                <div class="dp-feed-content">
                    <div class="dp-feed-text">${f.html}</div>
                    <div class="dp-feed-actor">
                        <span class="av" style="background:${f.actorColor}">${f.actorInitials}</span>
                        <span>${f.actorName}</span>
                        <span style="color:var(--dp-gray-300)">·</span>
                        <span>${f.time}</span>
                    </div>
                </div>`;
            container.appendChild(el);
        }
    }

    async function loadFeed(append = false) {
        if (feedLoading || feedExhausted) return;
        feedLoading = true;

        const body = document.getElementById('dp-feed-body')!;
        if (!append) {
            body.innerHTML = '<div class="dp-skeleton" style="height:52px;margin:8px 20px;border-radius:8px;"></div>'.repeat(3);
            feedCursor = 0;
            feedExhausted = false;
        }

        const data = await fetchJSON<FeedItem[]>(buildUrl(urls.feed, { cursor: String(feedCursor) }));
        clearSkeleton(body);
        feedLoading = false;

        if (!data || data.length === 0) {
            feedExhausted = true;
            if (!append && body.children.length === 0)
                body.innerHTML = '<div class="dp-empty-panel">No activity in the last 48 hours.</div>';
            return;
        }

        renderFeedItems(data, body);
        feedCursor += data.length;
    }

    // Infinite scroll inside the feed panel
    const sentinel = document.getElementById('dp-feed-sentinel');
    if (sentinel) {
        new IntersectionObserver(entries => {
            if (entries[0].isIntersecting) loadFeed(true);
        }, { rootMargin: '100px' }).observe(sentinel);
    }

    // ── Boot all panels ───────────────────────────────────────────────────
    loadAttention();
    loadRevenueSeries();
    loadPaymentDonut();
    loadDepartures();
    loadTopDestinations();
    loadOfficePerformance();
    loadFeed();
});
